import { GetQConfigByIdDocument, useUpdateQConfigAsDraftMutation } from '@exam-management/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Form,
  FormNumberInput,
  FormTextField,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';

import {
  QConfigEditFormProvider,
  useQConfigEditFormContext,
  type QConfigEditFormInput,
} from '../../providers';

interface QConfigEditFormProps {
  onClose: () => void;
  open: boolean;
  qConfigId: string;
}

function QConfigEditForm(props: Readonly<QConfigEditFormProps>) {
  const { onClose, open: isOpen, qConfigId } = props;
  const formContext = useQConfigEditFormContext();
  const { handleSubmit, formState } = formContext;
  const { showSnackbar } = useSnackbar();
  const [updateQConfigAsDraft] = useUpdateQConfigAsDraftMutation({
    onCompleted: () => {
      showSnackbar({ message: 'QConfig Information has been saved', variant: 'success' });
      onClose();
    },
    onError: () => {
      showSnackbar({ message: 'Unable to Save QConfig Information', variant: 'error' });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQConfigByIdDocument,
        variables: {
          getQConfigByIdInput: {
            qConfigId,
          },
        },
      },
    ],
  });

  const onSubmit = async (data: QConfigEditFormInput) => {
    await updateQConfigAsDraft({
      variables: {
        updateQConfigAsDraftInput: {
          qConfigId,
          name: data.qConfigName,
          totalQuestions: data.totalQuestions,
        },
      },
    });
  };

  return (
    <Dialog open={isOpen} maxWidth='sm'>
      <DialogTitle onClose={onClose}>Edit Information</DialogTitle>
      <Divider />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
            <FormTextField name='qConfigName' label='ชื่อ QConfig' placeholder='กรอกชื่อ QConfig' />
            <FormNumberInput
              name='totalQuestions'
              label='จำนวนข้อสอบ'
              placeholder='กรอกจำนวนข้อสอบ'
              sx={{ width: '180px' }}
            />
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            onClick={onClose}
            color='secondary'
            variant='outlined'
            size='large'
            disabled={formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button size='large' type='submit' loading={formState.isSubmitting}>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}
interface QConfigEditModalProps {
  onClose: () => void;
  open: boolean;
  qConfigId: string;
  name: string;
  totalQuestions: number;
}

export default function QConfigEditModal(props: Readonly<QConfigEditModalProps>) {
  const { onClose, open: isOpen, qConfigId, name, totalQuestions } = props;

  return (
    <QConfigEditFormProvider qConfigName={name} totalQuestions={totalQuestions}>
      <QConfigEditForm onClose={onClose} open={isOpen} qConfigId={qConfigId} />
    </QConfigEditFormProvider>
  );
}
